import { CampaignTemplate } from '@campaigns/src/types/campaignTypes';
import { useLocale } from '../../../../../i18n';
import useAuth from '../../../../app/src/features/authentication/hooks/useAuth';
import { getFlowInfo } from '../../../../flows/src/api/getFlowInfo';
import { FLOWS_PRODUCTS_PATH } from '../../../../flows/src/FlowsProductsRouter';
import {
  flowIdCitizinsTravel,
  flowIdTechniekNL,
} from '../../../../flows/src/constants/flowIds';
import useFlowsInitNew from '../../../../flows/src/hooks/init/useFlowsInitNew';
import useFlowsResetState from '../../../../flows/src/hooks/useFlowsResetState';
import { FlowInfo } from '../../../../flows/src/types/FlowsTypes';
import { SchemaYellowHiveProductsInConversationCmd } from 'wg-api-sdk';

const useStartDefaultSalesConversationFlow = () => {
  const { initFlow } = useFlowsInitNew();
  const { userLang } = useLocale();
  const { campaign } = useAuth();
  const { reset } = useFlowsResetState();

  const getOutputUrl = (flowInfo: FlowInfo) => {
    if (flowInfo.id === flowIdTechniekNL) {
      return '/sales-conversation/techniek-nederland/finalize';
    }
    if (flowInfo.id === flowIdCitizinsTravel) {
      return '/sales-conversation/offers/overview/travel';
    }
    // Route YellowHive flows to the Flow Products experience
    if (flowInfo.sub_type === 'YELLOWHIVE') {
      return `${FLOWS_PRODUCTS_PATH}/${flowInfo.id}/overview`;
    }
    if (flowInfo.type === 'offer') {
      return '/sales-conversation/offers/overview/default';
    }
    return `/sales-conversation/default/finalize`;
  };

  return async ({
    flowId,
    uuid,
    inputUrl,
    partyId,
    quoteId,
    quoteIds,
  }: {
    flowId: string;
    uuid?: string;
    inputUrl?: string;
    partyId?: string;
    quoteId?: string;
    quoteIds?: string[];
  }) => {
    reset();

    const flowInfo = await getFlowInfo(flowId, { lang: userLang });
    let yellowhiveProducts:
      | SchemaYellowHiveProductsInConversationCmd[]
      | undefined;
    // If this is a Yellowhive flow and campaign already includes products, reuse them
    if (
      [
        CampaignTemplate.YELLOWHIVE_CUSTOMER,
        CampaignTemplate.YELLOWHIVE_COMPANY,
      ].includes(campaign?.template?.key as CampaignTemplate) &&
      campaign?.yellowhive_products?.items?.length
    ) {
      yellowhiveProducts = campaign.yellowhive_products.items.map((item) => {
        const rawCoverages = item.coverages;
        const coverages = Array.isArray(rawCoverages)
          ? (rawCoverages as Array<{ code?: string } | string>)
              .map((c) => (typeof c === 'string' ? c : c.code))
              .filter((c): c is string => Boolean(c))
          : [];
        const product: SchemaYellowHiveProductsInConversationCmd = {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore SDK type may lag behind backend shape
          id: item.id,
          coverages,
        };
        return product;
      });
    }

    await initFlow({
      flowId: flowId,
      payload: {
        party_id: partyId,
        quote_id: quoteId,
        quote_ids: quoteIds,
        previous_session_id: flowInfo.previous ? uuid : undefined,
        subject: campaign?.subjects?.length ? campaign.subjects : undefined,
        yellowhive_products: yellowhiveProducts,
      },
      inputUrl,
      outputUrl: getOutputUrl(flowInfo),
      sessionId: uuid,
      shouldComplete: false,
    });
  };
};

export default useStartDefaultSalesConversationFlow;
